import React, { useEffect, useState } from "react";
import { Dialog } from '@headlessui/react';
import { useTheme } from '../../CommonComponents/ThemeProvider';

export default function AddStockModal({ isOpen, onClose, inventory, onSave, mode = "add", branchLabel = "" }) {
    const [addQuantity, setAddQuantity] = useState(0);
    const [error, setError] = useState("");
    const { currentTheme } = useTheme();
    const isRawMaterial = Boolean(inventory?.raw_material_id);
    const step = isRawMaterial ? "0.01" : "1";
    const unitLabel = inventory?.unit || inventory?.unit_type || "units";
    const isReleaseMode = mode === "release";

    useEffect(() => {
        if (isOpen) {
            setAddQuantity(0);
            setError("");
        }
    }, [isOpen]);

    if (!isOpen || !inventory) return null;

    const currentStock = Number(inventory.quantity) || 0;
    const totalStock = isReleaseMode
        ? currentStock - (Number(addQuantity) || 0)
        : currentStock + (Number(addQuantity) || 0);

    const handleSave = () => {
        const parsed = Number(addQuantity);
        if (!Number.isFinite(parsed) || parsed <= 0) {
            setError("Please enter a valid quantity greater than 0.");
            return;
        }
        if (isReleaseMode && parsed > currentStock) {
            setError("Release quantity cannot exceed admin stock.");
            return;
        }
        onSave(parsed, "");
    };

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            className="fixed inset-0 z-50 bg-black bg-opacity-40 flex items-center justify-center"
        >
            <div className="w-full max-w-sm px-4">
                <Dialog.Panel className="bg-white rounded-xl shadow-xl p-6 md:p-7 text-center space-y-5">
                    <Dialog.Title className="text-lg font-semibold text-gray-800 mb-2">
                        {isReleaseMode ? `Release Stock${branchLabel ? ` to ${branchLabel}` : ""}` : "Add Stock"}
                    </Dialog.Title>
                    <div className="text-left space-y-3">
                        <div className="text-sm text-gray-700">
                            <span className="font-medium">Current Stock:</span> {currentStock}
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1">{isReleaseMode ? "Release Quantity" : "Add Quantity"}</label>
                            <div className="flex items-center gap-2">
                                <input
                                    type="number"
                                    className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none text-lg text-center"
                                    value={addQuantity}
                                    min={0}
                                    step={step}
                                    inputMode={isRawMaterial ? "decimal" : "numeric"}
                                    onChange={e => {
                                        setAddQuantity(e.target.value === "" ? "" : Number(e.target.value));
                                        setError("");
                                    }}
                                    placeholder="Enter quantity to add"
                                />
                                {isReleaseMode && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setAddQuantity(currentStock);
                                            setError("");
                                        }}
                                        className="px-2 py-2 rounded border border-gray-300 text-xs font-semibold bg-gray-50 hover:bg-gray-100 whitespace-nowrap"
                                        title="Use maximum available stock"
                                    >
                                        Max
                                    </button>
                                )}
                            </div>
                            <p className="mt-1 text-xs text-gray-500">Unit: {unitLabel}</p>
                        </div>
                        <div className="text-sm text-gray-700">
                            <span className="font-medium">{isReleaseMode ? "Remaining Admin Stock:" : "New Total:"}</span> {Number.isFinite(totalStock) ? totalStock : currentStock}
                        </div>
                        {error && (
                            <div className="text-sm text-red-600">{error}</div>
                        )}
                    </div>
                    <div className="flex justify-center gap-3 pt-2">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 rounded-md text-sm font-medium transition bg-gray-100 text-gray-700 hover:bg-gray-200 focus:outline-none active:outline-none"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSave}
                            className="px-4 py-2 rounded-md text-sm font-medium transition focus:outline-none active:outline-none"
                            style={{ background: currentTheme.color, color: '#fff' }}
                        >
                            {isReleaseMode ? "Release" : "Add"}
                        </button>
                    </div>
                </Dialog.Panel>
            </div>
        </Dialog>
    );
}
